/*
 * THE REAL-TIME BUBBLES' ICONS, DRAWN AS BITMAPS (2026-10-18, Chris: "There is DEFINITELY STILL SKIPPING AND CHOPPINESS... MUST be smooth all the time").
 *
 * With icons on, every real-time bubble is an <image> of an SVG file (a flag, a browser's logo...). The browser does not keep an SVG image as pixels: each time the tile under it is
 * repainted it REPLAYS the SVG's drawing commands. ~80 icons moving all over the board, on a 120 Hz screen, were the single biggest load on the GPU process (the thread that
 * rasterises the page): measured in a real Chrome (perf/profile.mjs --gpu), switching icons on took that thread from 26% to 67% busy and the page from 118 to 111 frames a second.
 * The SAME icons pre-drawn once into a bitmap and shown from that took it to ~48% (and 112-114 fps): a bitmap is a texture the GPU just places.
 *
 * So the first time an icon is wanted at a size it is drawn into a canvas, at 3x that size (sharp on a 2x screen with room to spare), and kept as a PNG blob URL; every bubble showing that
 * icon at that size then uses the bitmap. Until the bitmap is ready the SVG is shown, so nothing is ever blank; if drawing fails (an SVG the canvas will not take) the SVG simply stays.
 * The drawing keeps the icon's own aspect ratio inside the box, centred - what an <image> with the default preserveAspectRatio ("xMidYMid meet") does - so the bitmap looks the same.
 */

/** the bitmap is drawn this many times larger than the icon is shown: sharp on a 2x screen, with some room for the bubble growing */
export const BITMAP_SCALE = 3;

export interface Fit {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Where an image of natural size (nw x nh) lands inside a box (bw x bh) so that all of it shows, centred, aspect kept: SVG's "xMidYMid meet". An image with no usable size fills the box. */
export const fitInside = (nw: number, nh: number, bw: number, bh: number): Fit => {
  if (!(nw > 0) || !(nh > 0)) return { x: 0, y: 0, width: bw, height: bh };
  const scale = Math.min(bw / nw, bh / nh);
  const width = nw * scale;
  const height = nh * scale;

  return { x: (bw - width) / 2, y: (bh - height) / 2, width, height };
};

/** The cache key: the file and the size it is shown at (rounded: a bubble a hair bigger than another does not need a bitmap of its own). */
export const bitmapKey = (href: string, width: number, height: number): string => `${href}|${Math.round(width)}x${Math.round(height)}`;

const ready = new Map<string, string>();
const pending = new Map<string, Promise<string>>();

/** the bitmap's URL if it has already been made */
export const cachedBitmap = (href: string, width: number, height: number): string | undefined => ready.get(bitmapKey(href, width, height));

const draw = (href: string, width: number, height: number): Promise<string> =>
  new Promise((resolve) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => {
      try {
        const cw = Math.max(1, Math.ceil(width * BITMAP_SCALE));
        const ch = Math.max(1, Math.ceil(height * BITMAP_SCALE));
        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        const context = canvas.getContext("2d");
        if (!context) return resolve(href);
        const fit = fitInside(image.naturalWidth, image.naturalHeight, cw, ch);
        context.drawImage(image, fit.x, fit.y, fit.width, fit.height);
        canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : href), "image/png");
      } catch (error) {
        resolve(href);   // a canvas that refuses the image (tainted, no size): keep showing the SVG
      }
    };
    image.onerror = () => resolve(href);
    image.src = href;
  });

/** The bitmap for an icon at a size: from the cache at once, or drawn (once, however many ask) and resolved when ready. Resolves to `href` itself when it could not be drawn. */
export const bitmapFor = (href: string, width: number, height: number): Promise<string> => {
  const key = bitmapKey(href, width, height);
  const hit = ready.get(key);
  if (hit) return Promise.resolve(hit);
  let promise = pending.get(key);
  if (!promise) {
    promise = draw(href, width, height).then((url) => {
      ready.set(key, url);
      pending.delete(key);

      return url;
    });
    pending.set(key, promise);
  }

  return promise;
};

/**
 * Show `node` (an <image> already given `href`) from its bitmap: at once if the bitmap exists, otherwise when it has been drawn - and only if the node is still on the page and still shows
 * that same file (a bubble whose icon changed in the meantime keeps its new one).
 */
export const useBitmapFor = (node: Element, href: string, width: number, height: number): void => {
  // (an SVG given as a data: URI is drawn like any other SVG; a data: bitmap or a blob: is already pixels)
  if (typeof document === "undefined" || !href || (href.startsWith("data:") && !href.startsWith("data:image/svg")) || href.startsWith("blob:")) return;
  const hit = cachedBitmap(href, width, height);
  if (hit) {
    if (hit !== href) node.setAttribute("href", hit);

    return;
  }
  bitmapFor(href, width, height).then((url) => {
    if (url !== href && node.isConnected && node.getAttribute("href") === href) node.setAttribute("href", url);
  });
};

/** for tests: forget every bitmap */
export const resetBitmaps = (): void => {
  ready.clear();
  pending.clear();
};

// for the real-browser checks (not in the production bundle): draw an icon with the real module and compare it to the SVG
if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") (window as any).__iconBitmaps = { bitmapFor, fitInside };
